import React, { useState } from "react";
import DataTable from "../../../components/DataTable";
import DuplicateModal, {
  DuplicateFormValues,
} from "../../../components/DuplicateModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Alert, Button, Select, Tabs, Typography, App } from "antd";
import { CourseEntity } from "../../../services/courses/courses.types";
import { ProjectEntity } from "../../../services/projects/projects.types";
import { useSubjects } from "../../../hooks/useSubject";
import { usePrograms } from "../../../hooks/usePrograms";
import TableSearchBar from "../../../components/TableSearchBar";
import { useTableUrlState } from "../../../hooks/useTableUrlState";
import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";
import { showDeleteUndoNotification } from "../../../utils/delete-undo";
import PageHeader from "../../../components/PageHeader";
import LinkButton from "../../../components/LinkButton";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";
import { apiSlice } from "../../../store/api/apiSlice";
import {
  useGetCoursesPaginatedQuery,
  useDeleteCourseMutation,
  useDuplicateCourseMutation,
} from "../../../store/api/coursesApi";
import {
  useGetProjectsPaginatedQuery,
  useDeleteProjectMutation,
} from "../../../store/api/projectsApi";

const COURSES_TAB = "cursos";
const PROJECTS_TAB = "proyectos";

// URL param name → commit mode; the URL is the source of truth for the
// active tab and for each tab's page/pageSize/filters, so the list is
// deep-linkable and survives refreshes. Project params are prefixed with
// "p" (pid/ptitle/pprogram/ppage) so switching tabs never leaks filters
// from one tab into the other.
const COURSES_FILTERS = {
  id: "debounced",
  title: "debounced",
  subject: "immediate",
  program: "immediate",
} as const;

const PROJECTS_FILTERS = {
  id: "debounced",
  title: "debounced",
  program: "immediate",
} as const;

const renderClave = (value?: string | null) =>
  value || <Typography.Text type="secondary">Sin clave</Typography.Text>;

const Courses: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subjects, loading: loadingSubjects } = useSubjects();
  const { programs, loading: loadingPrograms } = usePrograms();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get("tab") === PROJECTS_TAB ? PROJECTS_TAB : COURSES_TAB;

  const {
    page,
    pageSize,
    filters: courseFilters,
    inputs: courseInputs,
    setFilter: setCourseFilter,
    onPageChange: handlePageChange,
  } = useTableUrlState({ filters: COURSES_FILTERS });

  const {
    page: projectPage,
    pageSize: projectPageSize,
    filters: projectFilters,
    inputs: projectInputs,
    setFilter: setProjectFilter,
    onPageChange: handleProjectPageChange,
  } = useTableUrlState({ filters: PROJECTS_FILTERS, prefix: "p" });

  // `data` (instead of `currentData`) keeps the previous page visible while
  // the next one is fetching, so the table doesn't flicker to empty. Each
  // query is skipped while its tab is inactive, so the Proyectos endpoint is
  // never hit unless the user actually opens that tab (and vice versa).
  const {
    data: coursesData,
    isFetching: isFetchingCourses,
    isError: isCoursesError,
    refetch: refetchCourses,
  } = useGetCoursesPaginatedQuery(
    {
      page,
      pageSize,
      id: /^\d+$/.test(courseFilters.id)
        ? parseInt(courseFilters.id, 10)
        : undefined,
      title: courseFilters.title || undefined,
      subject_id: /^\d+$/.test(courseFilters.subject)
        ? parseInt(courseFilters.subject, 10)
        : undefined,
      program_code: courseFilters.program || undefined,
    },
    { skip: activeTab !== COURSES_TAB },
  );
  const {
    data: projectsData,
    isFetching: isFetchingProjects,
    isError: isProjectsError,
    refetch: refetchProjects,
  } = useGetProjectsPaginatedQuery(
    {
      page: projectPage,
      pageSize: projectPageSize,
      id: /^\d+$/.test(projectFilters.id)
        ? parseInt(projectFilters.id, 10)
        : undefined,
      title: projectFilters.title || undefined,
      program_id: /^\d+$/.test(projectFilters.program)
        ? parseInt(projectFilters.program, 10)
        : undefined,
    },
    { skip: activeTab !== PROJECTS_TAB },
  );
  const [deleteCourse] = useDeleteCourseMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [duplicateCourse, { isLoading: isDuplicating }] =
    useDuplicateCourseMutation();
  const [courseToDuplicate, setCourseToDuplicate] =
    useState<CourseEntity | null>(null);

  const courses = coursesData?.data ?? [];
  const coursesPagination = coursesData?.pagination ?? {
    page,
    pageSize,
    total: 0,
  };
  const projects = projectsData?.data ?? [];
  const projectsPagination = projectsData?.pagination ?? {
    page: projectPage,
    pageSize: projectPageSize,
    total: 0,
  };
  const isCoursesInitialLoad = useIsInitialLoad(isFetchingCourses, courses);
  const isProjectsInitialLoad = useIsInitialLoad(isFetchingProjects, projects);

  // Each tab starts clean: leaving one clears its inputs and strips its params.
  // The URL write goes last and is built from the render params, so it
  // supersedes the hooks' same-tick writes (all for the tab being left).
  const handleTabChange = (tab: string) => {
    if (tab === activeTab) return;
    const leavingCourses = activeTab === COURSES_TAB;
    const prefix = leavingCourses ? "" : "p";
    if (leavingCourses) {
      (Object.keys(COURSES_FILTERS) as Array<keyof typeof COURSES_FILTERS>).forEach(
        (key) => setCourseFilter(key, ""),
      );
    } else {
      (Object.keys(PROJECTS_FILTERS) as Array<keyof typeof PROJECTS_FILTERS>).forEach(
        (key) => setProjectFilter(key, ""),
      );
    }
    const next = new URLSearchParams(searchParams);
    [
      ...Object.keys(leavingCourses ? COURSES_FILTERS : PROJECTS_FILTERS),
      "page",
      "pageSize",
    ].forEach((key) =>
      next.delete(`${prefix}${key}`),
    );
    // The default tab is omitted from the URL (clean URLs).
    if (tab === COURSES_TAB) next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next);
  };

  // Deleting the last item of the last page must not leave an empty table.
  // Each tab clamps its own page params; the inactive tab keeps total = 0
  // (or its cached total), so it never clamps blindly.
  useClampPageToTotal({
    total: coursesPagination.total,
    pageSize,
    page,
    onPageChange: (lastPage) => handlePageChange(lastPage, pageSize),
  });
  useClampPageToTotal({
    total: projectsPagination.total,
    pageSize: projectPageSize,
    page: projectPage,
    onPageChange: (lastPage) =>
      handleProjectPageChange(lastPage, projectPageSize),
  });

  const handleEdit = (record: CourseEntity) => {
    navigate(`/courses/${record.id}`, { state: record.id });
  };

  const handleDelete = async (record: CourseEntity) => {
    try {
      await deleteCourse(record.id).unwrap();
      notification["success"]({
        message: "Éxito",
        description: "El curso fue eliminado exitosamente!",
        placement: "top",
        duration: 3,
      });
      showDeleteUndoNotification({
        entity: "course",
        id: record.id,
        title: record.title,
        // Restoring happens outside RTK Query, so the entity tag and the
        // trash cache must be invalidated by hand.
        onRestored: () => {
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Course", id: "LIST" },
              "Trash",
            ]),
          );
        },
      });
    } catch (error: any) {
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.data?.message || "El curso no pudo ser eliminado!",
        placement: "top",
        duration: 3,
      });
    }
  };

  const handleDuplicateConfirm = async ({
    title,
    children_mode,
  }: DuplicateFormValues) => {
    if (!courseToDuplicate) return;
    try {
      const copy = await duplicateCourse({
        id: courseToDuplicate.id,
        title,
        children_mode,
      }).unwrap();
      setCourseToDuplicate(null);
      notification["success"]({
        message: "Curso duplicado",
        description:
          children_mode === "link"
            ? `Se creó "${copy.title}" compartiendo las lecciones y recursos del original.`
            : `Se creó "${copy.title}" como copia independiente.`,
        placement: "top",
        duration: 5,
        btn: (
          <Button type="primary" onClick={() => navigate(`/courses/${copy.id}`)}>
            Editar copia
          </Button>
        ),
      });
    } catch (error: any) {
      notification["error"]({
        message: "Duplicación errónea",
        description:
          error?.data?.message || "El curso no pudo ser duplicado.",
        placement: "top",
        duration: 3,
      });
    }
  };

  const handleEditProject = (record: ProjectEntity) => {
    // Projects reuse the /courses/:id route, so the project flag must also
    // travel in the URL: otherwise a refreshed/shared link would load the
    // unrelated course that happens to have the same numeric id.
    navigate(`/courses/${record.id}?project=1`, {
      state: { id: record.id, isProject: true },
    });
  };

  const handleDeleteProject = async (record: ProjectEntity) => {
    try {
      await deleteProject(record.id).unwrap();
      notification["success"]({
        message: "Proyecto eliminado exitosamente.",
        description: "Éxito",
        placement: "topLeft",
      });
      showDeleteUndoNotification({
        entity: "project",
        id: record.id,
        title: record.title,
        onRestored: () => {
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Project", id: "LIST" },
              "Trash",
            ]),
          );
        },
      });
    } catch (error: any) {
      notification["error"]({
        message:
          error?.data?.message || "El proyecto no pudo ser eliminado.",
        description: "Error",
        placement: "topLeft",
      });
    }
  };

  return (
    <div>
      <PageHeader title="Cursos y Proyectos" />
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <Tabs.TabPane tab="Cursos" key={COURSES_TAB}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <TableSearchBar
              fields={[
                {
                  placeholder: "Buscar por ID",
                  value: courseInputs.id,
                  onChange: (value) => setCourseFilter("id", value),
                  width: 200,
                },
                {
                  placeholder: "Buscar por título o clave",
                  value: courseInputs.title,
                  onChange: (value) => setCourseFilter("title", value),
                  width: 200,
                },
              ]}
            >
              <Select
                style={{ width: 200 }}
                value={courseInputs.subject}
                onChange={(value) => setCourseFilter("subject", value ?? "")}
                allowClear
                placeholder="Filtrar por asignatura"
                options={[
                  { value: "", label: "Todas las asignaturas" },
                  ...subjects.map((subject) => ({
                    value: String(subject.id),
                    label: subject.name,
                  })),
                ]}
              />
              <Select
                style={{ width: 200 }}
                value={courseInputs.program}
                onChange={(value) => setCourseFilter("program", value ?? "")}
                allowClear
                placeholder="Filtrar por programa"
                options={[
                  { value: "", label: "Todos los programas" },
                  // The backend filters courses by program_code, so programs
                  // without a code (e.g. duplicates) can't be options here.
                  ...programs
                    .filter((program) => program.program_code)
                    .map((program) => ({
                      value: program.program_code,
                      label: `${program.program_code} - ${program.title}`,
                    })),
                ]}
              />
              <LinkButton type="primary" to="/courses/new">
                Nuevo curso
              </LinkButton>
            </TableSearchBar>
          </div>
          {isCoursesError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="No se pudieron cargar los cursos."
              action={
                <Button size="small" onClick={() => refetchCourses()}>
                  Reintentar
                </Button>
              }
            />
          )}
          {isCoursesInitialLoad ? (
            <ActivityIndicator />
          ) : (
            <DataTable
              data={courses}
              columns={[
                { title: "ID", dataIndex: "id", key: "id" },
                {
                  title: "Clave",
                  dataIndex: "clave",
                  key: "clave",
                  render: renderClave,
                },
                { title: "Nombre", dataIndex: "title", key: "title" },
                {
                  title: "Programas",
                  dataIndex: "programCodes",
                  key: "programCodes",
                  render: (programCodes: string[]) => {
                    if (!programCodes || programCodes.length === 0) {
                      return <strong>No asociado</strong>;
                    }
                    return <strong>{programCodes.join(", ")}</strong>;
                  },
                },
                {
                  title: "Asignatura",
                  dataIndex: "subject_id",
                  key: "subject_id",
                  render: (value) => {
                    return (
                      subjects.find((subject) => subject.id === value)?.name ||
                      "No asignada"
                    );
                  },
                },
              ]}
              onEdit={handleEdit}
              getEditHref={(record) => `/courses/${record.id}`}
              onDuplicate={(record) => setCourseToDuplicate(record)}
              onDelete={handleDelete}
              loading={isFetchingCourses || loadingSubjects || loadingPrograms}
              pagination={{
                current: coursesPagination.page,
                pageSize: coursesPagination.pageSize,
                total: coursesPagination.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
            />
          )}
        </Tabs.TabPane>

        <Tabs.TabPane tab="Proyectos" key={PROJECTS_TAB}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <TableSearchBar
              fields={[
                {
                  placeholder: "Buscar por ID",
                  value: projectInputs.id,
                  onChange: (value) => setProjectFilter("id", value),
                  width: 200,
                },
                {
                  placeholder: "Buscar por título o clave",
                  value: projectInputs.title,
                  onChange: (value) => setProjectFilter("title", value),
                  width: 200,
                },
              ]}
            >
              <Select
                style={{ width: 200 }}
                value={projectInputs.program}
                onChange={(value) => setProjectFilter("program", value ?? "")}
                allowClear
                placeholder="Filtrar por programa"
                options={[
                  { value: "", label: "Todos los programas" },
                  ...programs.map((program) => ({
                    value: String(program.id),
                    label: program.program_code
                      ? `${program.program_code} - ${program.title}`
                      : program.title,
                  })),
                ]}
              />
              <LinkButton
                type="primary"
                to="/courses/new?project=1"
                state={{ defaultTab: "project" }}
              >
                Nuevo proyecto
              </LinkButton>
            </TableSearchBar>
          </div>
          {isProjectsError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="No se pudieron cargar los proyectos."
              action={
                <Button size="small" onClick={() => refetchProjects()}>
                  Reintentar
                </Button>
              }
            />
          )}
          {isProjectsInitialLoad ? (
            <ActivityIndicator />
          ) : (
            <DataTable
              data={projects}
              columns={[
                { title: "ID", dataIndex: "id", key: "id" },
                {
                  title: "Clave",
                  dataIndex: "clave",
                  key: "clave",
                  render: renderClave,
                },
                { title: "Nombre", dataIndex: "title", key: "title" },
                {
                  title: "Programas",
                  dataIndex: "programCodes",
                  key: "programCodes",
                  render: (programCodes: string[]) => {
                    if (!programCodes || programCodes.length === 0) {
                      return <strong>No asociado</strong>;
                    }
                    return <strong>{programCodes.join(", ")}</strong>;
                  },
                },
              ]}
              onEdit={handleEditProject}
              getEditHref={(record) => `/courses/${record.id}?project=1`}
              onDelete={handleDeleteProject}
              loading={isFetchingProjects || loadingPrograms}
              pagination={{
                current: projectsPagination.page,
                pageSize: projectsPagination.pageSize,
                total: projectsPagination.total,
                onChange: handleProjectPageChange,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
            />
          )}
        </Tabs.TabPane>
      </Tabs>
      <DuplicateModal
        open={courseToDuplicate !== null}
        entityLabel="curso"
        originalTitle={courseToDuplicate?.title ?? ""}
        childrenLabel="las lecciones y recursos del curso"
        loading={isDuplicating}
        onCancel={() => setCourseToDuplicate(null)}
        onConfirm={handleDuplicateConfirm}
      />
    </div>
  );
};

export default Courses;
